import NiceModal from '@ebay/nice-modal-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { type ChangeOrderRefType, getChangeOrderApprovals } from '@/api/Sherpa/ChangeOrder';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import ChangeOrderDetails from '@/features/ChangeOrder/Details';
import { EditMilestoneModal } from '@/features/ChangeOrder/Summary/ChangeOrderMilestones/EditMilestone';
import { useMilestoneChangeOrderDetails } from '@/hooks/ReactQueryHooks/useChangeOrderDetailsQuery';
import { formatDate3 } from '@/utils';

export function ChangeOrderMilestoneDetails() {
  const { projectId = '', changeOrderId = '' } = useParams();
  const queryClient = useQueryClient();
  const { data } = useMilestoneChangeOrderDetails({
    id: Number(changeOrderId),
  });
  const resourceType: ChangeOrderRefType = 'changeOrderMilestone';

  const { data: approvals } = useQuery(
    [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_APPROVALS, changeOrderId],
    () =>
      getChangeOrderApprovals({
        refType: resourceType,
        refId: Number(changeOrderId),
      }),
    {
      select: (data) => data.data,
    }
  );

  const invalidateQueries = () => {
    queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_CHANGE_ORDER_MILESTONES, Number(changeOrderId)]);
    queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_CHANGE_ORDER_APPROVALS]);
    queryClient.invalidateQueries([`${resourceType}-audit`]);
  };

  const handleEdit = () => {
    if (!data) return;

    NiceModal.show(EditMilestoneModal, {
      projectId,
      changeOrderId,
      defaultValues: {
        phase: {
          label: data.phase.name,
          value: data.phase.slug,
        },
        milestone: {
          value: String(data.cOrderTypeId),
          label: data.cOrderTypeName,
          dueDate: data.milestone.originalDueDate,
        },
        dueDate: formatDate3(data.milestone.originalDueDate, 'yyyy-mm-dd'),
        proposedDueDate: formatDate3(data?.milestone.proposedDueDate, 'yyyy-mm-dd'),
        changeInDays: data.daysImpacted,
        noResponseConsequence: data.consequences,
        notes: data.notes,
        reason: data.reason,
        responseDueBy: formatDate3(data.milestone.responseDueByDate, 'yyyy-mm-dd'),
        recentFiles: approvals?.at(0)?.approvalAsset,
      },
      onSubmitted: invalidateQueries,
    });
  };

  return (
    <ChangeOrderDetails
      title={data?.cOrderTypeName}
      type='milestone'
      resourceType={resourceType}
      commentResourceType='ChangeOrderMilestone'
      onEdit={handleEdit}
      status={data?.status || ''}
      submittedBy={data?.submittedBy}
      additionalInfo={[
        {
          title: 'Why is the change required?',
          value: data?.reason || '',
        },
        {
          title: 'No response consequence',
          value: data?.consequences || '',
        },
        {
          title: 'Notes',
          value: data?.notes || '–',
        },
      ]}
      details={[
        {
          title: 'Original Due Date',
          value: data?.milestone?.originalDueDate ? formatDate3(data?.milestone?.originalDueDate) : '',
        },
        {
          title: 'Response Due Date',
          value: data?.milestone?.responseDueByDate ? formatDate3(data?.milestone?.responseDueByDate) : '',
        },
        {
          title: 'Proposed Due Date',
          value: data?.milestone?.proposedDueDate ? formatDate3(data?.milestone?.proposedDueDate) : '',
        },
        {
          title: 'Days Left to Respond',
          value: String(data?.daysLeft) || '',
        },
        {
          title: '',
          value: '',
        },
        {
          title: 'Impact',
          value: `${data?.daysImpacted} Days`,
        },
      ]}
    />
  );
}
